import React, { useState, useEffect, useCallback, useContext, useMemo } from "react";

import { ActionBar, ConfirmDialog, Sticky } from "@plex/react-components";

import { Roles, Tenants } from "config";
import { stringHelpers } from "infrastructure/helpers";
import { commonMessages } from "infrastructure/localization";
import { UserContext } from "ui/app";
import { ActionBarLink } from "ui/app/navigation";
import { useLocalizedValues } from "ui/hooks";
import { securityMessages } from "ui/screens/security/security.localization";

import { GrantRole } from "../grant-role";
import { ISecurityRoleMember, ISecurityRoleMembersFilter } from "../SecurityRoleMembers.state";

export interface ISecurityRoleMembersActionBarProps {
  selectedMembers: ISecurityRoleMember[];
  securityRoleMembersFilter?: ISecurityRoleMembersFilter;
  revokeSecurityRole: (rows: ISecurityRoleMember[]) => void;
}

export const SecurityRoleMembersActionBar: React.FC<ISecurityRoleMembersActionBarProps> = ({ selectedMembers, securityRoleMembersFilter, revokeSecurityRole }) => {
  const localizedValues = useLocalizedValues(() => ({
    grantRoleAction: securityMessages.action("grantRole"),
    revokeRoleAction: securityMessages.action("revokeRole"),
    revokeConfirmationQuestion: commonMessages.info("deleteRecordQuestion"),
    askTitle: commonMessages.title("confirmationDialog"),
    noButton: commonMessages.option("no"),
    yesButton: commonMessages.option("yes")
  }));

  const userContext = useContext(UserContext);

  const grantRolePermission = useMemo<{ canGrantRole: boolean }>(() => {
    let result = false;

    if (securityRoleMembersFilter?.securityRole) {
      if (securityRoleMembersFilter.tenant) {
        result = userContext.isAuthorized([{ roles: [Roles.SecurityAdmin], tenants: [securityRoleMembersFilter.tenant.tenantId] }]);
      }
      if (!result && securityRoleMembersFilter.enterprise) {
        result = userContext.isAuthorized([{ roles: [Roles.SecurityAdmin], enterprises: [securityRoleMembersFilter.enterprise.enterpriseId] }]);
      }

      if (!result) {
        result =
          stringHelpers.equal(securityRoleMembersFilter?.securityRole.securityRoleId, Roles.SecurityAdmin) &&
          userContext.isAuthorized([{ roles: [Roles.PlexSecurityAdmin], tenants: [Tenants.Plex] }]);
      }
    }

    return {
      canGrantRole: result
    };
  }, [securityRoleMembersFilter, userContext]);

  const shouldDisableGrantRoleButton = useCallback(() => {
    return !grantRolePermission.canGrantRole;
  }, [grantRolePermission]);

  const shouldDisableRevokeRoleButton = useCallback(() => {
    return !selectedMembers?.length;
  }, [selectedMembers]);

  const [grantRoleButtonState, setGrantRoleButtonState] = useState<{ disabled: boolean }>(() => ({
    disabled: shouldDisableGrantRoleButton()
  }));

  const [revokeRoleButtonState, setRevokeRoleButtonState] = useState<{ disabled: boolean }>(() => ({
    disabled: shouldDisableRevokeRoleButton()
  }));

  const [revokeRoleConfirmationDialogState, setRevokeRoleConfirmationDialogState] = useState({ showConfirmation: false });

  const onRoleRevoke = useCallback(() => setRevokeRoleConfirmationDialogState({ showConfirmation: true }), []);

  const onRevokeConfirmationDialogHide = useCallback(
    (answer: string) => {
      setRevokeRoleConfirmationDialogState({ showConfirmation: false });

      if (answer === localizedValues.yesButton) {
        revokeSecurityRole(selectedMembers);
      }
    },
    [revokeSecurityRole, selectedMembers, localizedValues.yesButton]
  );

  useEffect(() => {
    setGrantRoleButtonState(prev => ({
      ...prev,
      disabled: shouldDisableGrantRoleButton()
    }));
  }, [shouldDisableGrantRoleButton]);

  useEffect(() => {
    setRevokeRoleButtonState(prev => ({
      ...prev,
      disabled: shouldDisableRevokeRoleButton()
    }));
  }, [shouldDisableRevokeRoleButton]);

  return (
    <>
      <Sticky>
        <ActionBar>
          <ActionBarLink
            disabled={grantRoleButtonState.disabled}
            text={localizedValues.grantRoleAction}
            dialog={{
              content: securityRoleMembersFilter?.securityRole && (
                <GrantRole securityRole={securityRoleMembersFilter?.securityRole} tenant={securityRoleMembersFilter.tenant} enterprise={securityRoleMembersFilter.enterprise} />
              )
            }}
          />
          <ActionBar.Button disabled={revokeRoleButtonState.disabled} text={localizedValues.revokeRoleAction} title={localizedValues.revokeRoleAction} onClick={onRoleRevoke} />
        </ActionBar>
      </Sticky>
      <ConfirmDialog
        show={revokeRoleConfirmationDialogState.showConfirmation}
        title={localizedValues.askTitle}
        question={localizedValues.revokeConfirmationQuestion}
        answers={[localizedValues.yesButton, localizedValues.noButton]}
        defaultAnswer={localizedValues.noButton}
        onHide={onRevokeConfirmationDialogHide}
      />
    </>
  );
};
